import React, { useMemo } from 'react';
import { marked } from 'marked';

// Global marked configuration for every chat surface (brief builder,
// strategy chat, exploration). Lives here so no panel depends on another
// panel's import side effects.
marked.setOptions({
  gfm: true,
  breaks: true,
});

export const CHAT_MARKDOWN_ALLOWED_TAGS = [
  'p',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'br',
  'hr',
  'code',
  'pre',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'a',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'mark',
];

export const CHAT_MARKDOWN_ALLOWED_ATTRS = ['href', 'target', 'rel', 'data-numbered'];

// Matches list items whose text already begins with manual numbering
// (e.g. "1. Foo" inside an <ol>) so CSS can suppress the double marker.
const NUMBERED_LI_REGEX = /^\d+(\.\d+)*([.)-]|\s)\s*/;

export const sanitizeChatHtml = (html: string): string => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const allowedTags = new Set(CHAT_MARKDOWN_ALLOWED_TAGS);
  const allowedAttrs = new Set(CHAT_MARKDOWN_ALLOWED_ATTRS);

  const cleanseNode = (root: Node) => {
    Array.from(root.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();

        if (!allowedTags.has(tagName)) {
          while (element.firstChild) {
            element.parentNode?.insertBefore(element.firstChild, element);
          }
          element.parentNode?.removeChild(element);
          return;
        }

        Array.from(element.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          if (!allowedAttrs.has(attrName)) {
            element.removeAttribute(attr.name);
            return;
          }
          if (attrName === 'href') {
            const hrefValue = element.getAttribute('href') || '';
            if (!/^(https?:|mailto:|\/|#)/i.test(hrefValue)) {
              element.removeAttribute('href');
            }
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noreferrer noopener');
          }
        });

        if (
          tagName === 'li' &&
          NUMBERED_LI_REGEX.test((element.textContent || '').trim())
        ) {
          element.setAttribute('data-numbered', 'true');
        }

        cleanseNode(element);
      } else if (node.nodeType === Node.COMMENT_NODE) {
        node.parentNode?.removeChild(node);
      }
    });
  };

  cleanseNode(template.content);
  return template.innerHTML;
};

export const renderChatMarkdown = (text: string): string => {
  if (!text || !text.trim()) return '';
  try {
    const rawHtml = marked.parse(text, { async: false }) as string;
    return sanitizeChatHtml(rawHtml);
  } catch (err) {
    return '';
  }
};

/**
 * Shared markdown renderer for chat message bodies. Visual styling comes
 * entirely from the `className` passed by each surface (e.g.
 * "structured-markdown"), so this component never changes bubble aesthetics.
 */
const ChatMarkdown: React.FC<{ text: string; className?: string }> = ({
  text,
  className = 'structured-markdown',
}) => {
  const html = useMemo(() => renderChatMarkdown(text), [text]);
  if (!html) {
    return <>{text}</>;
  }
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default ChatMarkdown;
